import { Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import { AppState } from 'common/appState';
import { ArchivedFilterOptions } from 'common/helpers/utils';
import { saveFilters } from 'features/pagination/actions/paginationActions';

import { PaginationView } from '../models/paginationModel';
import { FilterData } from 'common/helpers/url';
import { getFilterParams } from '../selectors/paginationSelectors';

type Props = {
    paginatedView: PaginationView;
    path?: string;
    /* FilterTab renders this control without one; the caller decides whether a
       change needs a refetch. */
    refreshTable?: (filters?: FilterData) => void;
};

/**
 * The stored filter is written by this dropdown, by the "All Filters" drawer's
 * radio group, and (for All) by two different sentinels, so every shape has to
 * map back onto an option here or the control shows the wrong one.
 *
 * An absent value means the user has not chosen: the views in
 * UNARCHIVED_BY_DEFAULT_VIEWS are filtered to Unarchived by paginationEpic, so
 * Unarchived is what the control should read.
 *
 * UNCERTAIN(inert-views): on EditEmployees and the Filters tabs this control
 * writes showArchived to endpoints that only expose `archived`, so nothing is
 * applied and reading "Unarchived" there is still wrong. BenefitTemplates' endpoint
 * also expects `archived`, and withArchivedParam (helpers/archivedParam.ts)
 * renames its choice; those views could reuse it once their default is decided.
 */
const toArchivedFilterOption = (showArchived?: boolean | string): ArchivedFilterOptions => {
    if (showArchived === true || showArchived === ArchivedFilterOptions.Archived) {
        return ArchivedFilterOptions.Archived;
    }

    if (
        showArchived === undefined ||
        showArchived === false ||
        showArchived === ArchivedFilterOptions.Unarchived
    ) {
        return ArchivedFilterOptions.Unarchived;
    }

    return ArchivedFilterOptions.All;
};

export const PaginationArchivedFilter = ({ paginatedView, path, refreshTable }: Props) => {
    const dispatch = useDispatch();

    /* Derived from the store rather than held in local state: this control
       unmounts on navigation, so local state came back as Unarchived while the
       saved filter was still Archived or All, and the dropdown then disagreed
       with the rows under it. */
    const archivedFilter = useSelector((state: AppState) =>
        toArchivedFilterOption(getFilterParams(state, paginatedView)?.values.showArchived),
    );

    /* antd passes undefined when the value is cleared, which stores undefined and
       so falls back to the Unarchived default — the same thing Clear Filters does
       everywhere else, rather than a fourth state that renders blank.

       UNCERTAIN(clear-semantics): before this change, clearing meant "All"
       (the old switch left showArchived undefined and nothing re-applied a
       default). It now means "back to Unarchived". Chosen for consistency with
       Clear Filters; All is still reachable as its own option. Confirm with
       product which one they want. */
    const handleOnArchivedFilterChange = (value?: ArchivedFilterOptions) => {
        /* All is stored as ArchivedFilterOptions.All rather than as undefined so
           that it survives the Unarchived default in paginationEpic. It is dropped
           from the query string by generateFilterQueryParams, which is what All
           means to the API: no show_archived parameter at all. */
        const showArchived =
            value === ArchivedFilterOptions.Archived
                ? true
                : value === ArchivedFilterOptions.Unarchived
                ? false
                : value === ArchivedFilterOptions.All
                ? ArchivedFilterOptions.All
                : undefined;

        dispatch(
            saveFilters({
                paginatedView,
                values: { showArchived },
                path,
            }),
        );
        if (refreshTable) {
            refreshTable();
        }
    };

    return (
        <Select
            value={archivedFilter}
            onChange={handleOnArchivedFilterChange}
            allowClear
            dropdownMatchSelectWidth={false}
            /* size="large" is what earns the .ant-select-lg rules in
               TableFiltersBar's FilterWrapper: width 100% and height 40px, so the
               control matches the filters beside it. Without it this was the only
               Select on those bars left at its intrinsic ~118px, and antd caps the
               label at calc(100% - 40px) for the arrow and clear icon, which left
               53px for "Unarchived" and rendered it as "Un...".

               minWidth is the floor for the one caller that is not inside a
               FilterWrapper (ContractBenefitsButtons uses a bare antd Col, where
               the width rule does not apply): "Unarchived" needs 94px plus antd's
               40px reserve plus 22px of selector padding. */
            size="large"
            style={{ width: '100%', minWidth: 160 }}
        >
            {(Object.keys(ArchivedFilterOptions) as (keyof typeof ArchivedFilterOptions)[]).map(
                (name) => (
                    <Select.Option key={name} value={ArchivedFilterOptions[name]}>
                        {name}
                    </Select.Option>
                ),
            )}
        </Select>
    );
};
